/**
 * System / governance data hooks (S10.1).
 *
 * Reads use ahooks `useRequest` with `/api/`-prefixed cacheKeys; mutations run
 * through `systemApi`, clear the affected cacheKeys, then the caller refreshes
 * (deliberate reload after an explicit admin action — not a background refetch).
 * Every mutation also invalidates the audit cache so the review lens picks up
 * the row the handler just appended.
 */

import { clearCache, useRequest } from 'ahooks';
import { systemApi } from './systemApi';
import type { CreateOrganisationInput } from './systemApi';
import apiClient, { getUserProfile } from '@/services/api/axios.config';
import type { User } from '@/models/user';
import type {
  AuditLogQuery,
  GovernanceRecordStatus,
  GovernanceRole,
  GovernanceUserStatus,
} from './model';

const ORG_KEY = '/api/governance/organisations';
const ROLES_KEY = '/api/governance/roles';
const USERS_KEY = '/api/governance/users';
const AUDIT_KEY = '/api/governance/audit-logs';

const actorName = (): string | undefined => (getUserProfile() as User | null)?.name ?? undefined;

/** Invalidate the audit cache after any governance write. */
function invalidateAudit() {
  clearCache(AUDIT_KEY);
}

export function useOrganisations() {
  const { data, error, loading, refresh } = useRequest(systemApi.listOrganisations, {
    cacheKey: ORG_KEY,
  });
  return { organisations: data?.data, error, loading, refresh };
}

export function useRoles() {
  const { data, error, loading, refresh } = useRequest(systemApi.listRoles, {
    cacheKey: ROLES_KEY,
  });
  return { roles: data?.data, error, loading, refresh };
}

export function useUsers() {
  const { data, error, loading, refresh } = useRequest(systemApi.listUsers, {
    cacheKey: USERS_KEY,
  });
  return { users: data?.data, error, loading, refresh };
}

export function useAuditLogs(query: AuditLogQuery) {
  const { data, error, loading, refresh } = useRequest(() => systemApi.listAuditLogs(query), {
    cacheKey: `${AUDIT_KEY}?${JSON.stringify(query)}`,
    refreshDeps: [query.dateFrom, query.dateTo, query.actor, query.module],
  });
  return { auditLogs: data?.data, error, loading, refresh };
}

/**
 * One reason-gated rbacV2 matrix save per role (S2.2), in two legs:
 *   1. `permissions` → the governance `PATCH /governance/roles/:id` (unchanged
 *      reason-gated audit row + member reprojection) — sent only when the
 *      grants actually changed, so no empty audit rows.
 *   2. `inheritsFrom` → the S2.3 `PATCH /governance/roles/:id/hierarchy`
 *      endpoint (cycle-rejected server-side, system role → 409) — sent only
 *      when the edges changed.
 * Kept slice-local on `apiClient` (the S2.1 `reparentOrganisation` precedent)
 * because `systemApi.ts` is owned by the concurrent S2.3 slice — followUp
 * recorded to fold both calls into `systemApi` once that file frees.
 */
export interface RoleDefinitionInput {
  /** New direct grants — omit when unchanged (avoids a no-op audit row). */
  permissions?: string[];
  /** New `inheritsFrom` edges — omit when unchanged. */
  inheritsFrom?: string[];
}

async function patchRoleDefinition(
  id: string,
  input: RoleDefinitionInput,
  reason: string,
  actor?: string
): Promise<void> {
  if (input.permissions) {
    await apiClient.patch<GovernanceRole>(`/governance/roles/${id}`, {
      permissions: input.permissions,
      reason,
      actorName: actor,
    });
  }
  if (input.inheritsFrom) {
    // The hierarchy handler audits `reason`/`actorName` too (S2.3) — thread the
    // SAME reason-gate values as the permissions leg so the edge edit's audit
    // row is never anonymous/reason-less.
    await apiClient.patch<GovernanceRole>(`/governance/roles/${id}/hierarchy`, {
      inheritsFrom: input.inheritsFrom,
      reason,
      actorName: actor,
    });
  }
}

export function useSystemMutations() {
  return {
    createOrganisation: async (input: CreateOrganisationInput) => {
      const org = await systemApi.createOrganisation({ ...input, actorName: actorName() });
      clearCache(ORG_KEY);
      invalidateAudit();
      return org;
    },
    setOrganisationStatus: async (
      id: string,
      status: GovernanceRecordStatus,
      reason: string
    ) => {
      const org = await systemApi.setOrganisationStatus(id, status, reason, actorName());
      clearCache(ORG_KEY);
      invalidateAudit();
      return org;
    },
    setRolePermissions: async (id: string, permissions: string[], reason: string) => {
      const role = await systemApi.setRolePermissions(id, permissions, reason, actorName());
      clearCache(ROLES_KEY);
      clearCache(USERS_KEY);
      invalidateAudit();
      return role;
    },
    /** rbacV2 matrix save (S2.2): changed permissions and/or `inheritsFrom` edges. */
    setRoleDefinition: async (id: string, input: RoleDefinitionInput, reason: string) => {
      await patchRoleDefinition(id, input, reason, actorName());
      clearCache(ROLES_KEY);
      clearCache(USERS_KEY);
      invalidateAudit();
    },
    setUserStatus: async (id: string, status: GovernanceUserStatus, reason: string) => {
      const user = await systemApi.setUserStatus(id, status, reason, actorName());
      clearCache(USERS_KEY);
      invalidateAudit();
      return user;
    },
    setUserRoles: async (id: string, roleIds: string[], reason: string) => {
      const user = await systemApi.setUserRoles(id, roleIds, reason, actorName());
      clearCache(USERS_KEY);
      invalidateAudit();
      return user;
    },
  };
}
